import {
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ClientProductRatePlanModel,
  ClientProductRoomModel,
  ClientSelectedRoom,
  ContainerModel,
  KeyValueFilter,
} from '@app/models/server/layout.model';
import {
  DOCUMENT,
  NgClass,
  NgOptimizedImage,
  NgTemplateOutlet,
  SlicePipe,
  TitleCasePipe,
} from '@angular/common';
import { EventService } from '@app/services/util-services/root-services/event-service';
import { TextClampShellComponent } from '@app/components/ui-libs/shell/text-clamp-shell/text-clamp-shell.component';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DisplayCategoryPipe } from '@app/components/pipes/display-category/display-category.pipe';
import { RoomGuestFormComponent } from '@app/new-ui-lib/b2b-cards/components/b2b-property-room-card/room-guest-form/room-guest-form.component';
import { SelectedItem } from '@app/models/client/item.model';
import { SeoDbSchema } from '@b2b/seo-util-lib';
import { InitAuthService } from '@app/services/global-services/auth/init-auth-service';
import { B2bDragScrollComponent } from '@app/new-ui-lib/b2b-drag-scroll/b2b-drag-scroll/b2b-drag-scroll.component';
import { B2bDragScrollItemComponent } from '@app/new-ui-lib/b2b-drag-scroll/b2b-drag-scroll-item/b2b-drag-scroll-item.component';
import { DragScrollType } from '@app/models/client/drag-scroll.type';
import { IonContent, IonModal } from '@ionic/angular/standalone';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { FEATURE_TYPES } from '@app/constants/app-settings';
import { LoaderUtilService } from '@app/services/loader/loader-util.service';

@Component({
  selector: 'app-b2b-property-room-card',
  templateUrl: './b2b-property-room-card.component.html',
  styleUrls: ['./b2b-property-room-card.component.scss'],
  standalone: true,
  imports: [
    TitleCasePipe,
    NgClass,
    NgOptimizedImage,
    TextClampShellComponent,
    FormsModule,
    DisplayCategoryPipe,
    RoomGuestFormComponent,
    B2bDragScrollComponent,
    B2bDragScrollItemComponent,
    NgTemplateOutlet,
    IonContent,
    IonModal,
    SlicePipe,
  ],
})
export class B2bPropertyRoomCardComponent implements OnInit, OnDestroy {
  @Input() compData: {
    container: ContainerModel;
  };

  @Input() seoPageData: SeoDbSchema;

  @Input() multiRoomWarningContainer!: ContainerModel | undefined;

  @Input() cartContainer: ContainerModel[] = [];

  isDropdownOpen = false;
  swiperConfig: DragScrollType = {
    autoHeight: true,
    spaceBetween: 10,
    navigation: false,
    freeMode: false,
    mousewheel: false,
    grabCursor: false,
    pagination: true,
    slidesPerView: 1.2,
    centeredSlides: false,
    injectStyles: [],
    hideScrollButtons: false,
    showAbsoluteButtons: true,
  };
  // selectedImage: ContainerImageModel;
  // isDropdownActive = false;

  scrollToRoom: ClientProductRoomModel = null;

  roomDataContainerUpdatedSubscription: Subscription;
  refreshDataSubscription: Subscription;
  imgQuality = 90;
  imgHeight = '400px';

  roomsOpenInModal = false;
  selectedRoomContainer?: ClientProductRoomModel;
  selectedRoomContainerIndex?: number;
  selectedRatePlanFilter: KeyValueFilter;
  emptyRooms: string[] = [];

  multipleRoomWarningModalOpenCount: number = Number(0);
  multipleRoomWarningState: {
    roomData: ClientProductRoomModel;
    ratePlan: ClientProductRatePlanModel;
    ratePlanIndex: number;
    roomIndex: number;
  } = undefined;

  @ViewChild('individualRoomModal') individualRoomModal: IonModal;
  @ViewChild('multipleRoomWarningModal') multipleRoomWarningModal: IonModal;
  isServer: boolean = this.b2bDeviceUtilService.isServer();

  constructor(
    private eventService: EventService,
    @Inject(DOCUMENT) private readonly document,
    private initAuthService: InitAuthService,
    private b2bDeviceUtilService: B2bDeviceUtilService,
    private loaderUtilService: LoaderUtilService,
  ) {}

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  ngOnInit() {
    this.roomDataContainerUpdatedSubscription =
      this.eventService.roomDataContainerUpdatedEvent.subscribe({
        next: (response: { roomDataContainer?: ClientProductRoomModel[] }) => {
          this.updateRoomDataFromRoomDataContainer(response?.roomDataContainer);
          if (this.selectedRatePlanFilter?.key) {
            const temp = JSON.parse(
              JSON.stringify(this.selectedRatePlanFilter),
            );
            this.selectedRatePlanFilter = undefined;
            this.onRatePlanFilter(temp);
          }
        },
      });

    this.refreshData();
  }

  refreshData() {
    if (this.seoPageData?.seoUrl?.seoData?.url) {
      this.refreshDataSubscription = this.initAuthService
        ?.isAuthenticated()
        ?.subscribe((result) => {
          if (result) {
            this.fireSelectRoomEvent(this.compData?.container?.roomContainers);
          }
        });
    }
  }

  /*toggleDropdown() {
    this.isDropdownActive = !this.isDropdownActive;
  }*/

  gotoRoom(room: ClientProductRoomModel) {
    if (room) {
      this.document?.getElementById(room?.key)?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  removeSelectedRatePlan(
    roomData: ClientProductRoomModel,
    ratePlan: ClientProductRatePlanModel,
    ratePlanIndex: number,
  ) {
    ratePlan.selectedRooms = [];
    this.setRoomDisableButton(roomData);
    this.fireSelectRoomEvent(
      this.compData?.container?.roomContainers,
      'REMOVE_ROOM',
    );
  }

  selectRatePlan(
    roomData: ClientProductRoomModel,
    ratePlan: ClientProductRatePlanModel,
    ratePlanIndex: number,
    roomIndex: number,
  ) {
    ratePlan.selectedRooms = [];
    if (
      this.multiRoomWarningContainer?.title &&
      this.multiRoomWarningContainer?.totalRooms === 1 &&
      this.multipleRoomWarningModalOpenCount === 0 &&
      this.compData?.container?.feature?.type === FEATURE_TYPES?.EXTENDEDSTAY
    ) {
      if (this.cartContainer?.[0]?.newCartDescription?.totalRooms === 1) {
        // TODO show popup
        this.multipleRoomWarningState = {
          roomData,
          ratePlan,
          ratePlanIndex,
          roomIndex,
        };

        this.multipleRoomWarningModalOpenCount = 1;

        this.openMultipleRoomWarningModal();
        return;
      }
    }

    this.selectRoomAndAddRoom(roomData, ratePlan, ratePlanIndex, roomIndex);
  }

  addNewRoom(
    roomData: ClientProductRoomModel,
    ratePlan: ClientProductRatePlanModel,
    ratePlanIndex: number,
    roomIndex: number,
  ) {
    if (!ratePlan?.selectedRooms) {
      ratePlan.selectedRooms = [];
    }

    this.selectRoomAndAddRoom(roomData, ratePlan, ratePlanIndex, roomIndex);
  }

  private selectRoomAndAddRoom(
    roomData: ClientProductRoomModel,
    ratePlan: ClientProductRatePlanModel,
    ratePlanIndex: number,
    roomIndex: number,
    switchRoom?: boolean,
  ) {
    let operationType: string;

    if (switchRoom) {
      operationType = 'SWITCH_WITHIN_ALL_ROOM';
    }

    const tempRoomData: ClientProductRoomModel = JSON.parse(
      JSON.stringify(roomData),
    );
    const tempRatePlan: ClientProductRatePlanModel = JSON.parse(
      JSON.stringify(ratePlan),
    );

    tempRatePlan.isNew = true;

    tempRoomData.ratePlanData[ratePlanIndex] = tempRatePlan;

    let totalSelectedRooms = 0;

    for (const ratePlanTemp of tempRoomData?.ratePlanData) {
      const selectedRoomCount: number =
        ratePlanTemp?.selectedRooms?.length > 0
          ? ratePlanTemp?.selectedRooms?.length
          : 0;
      totalSelectedRooms = totalSelectedRooms + selectedRoomCount;
    }

    if (
      totalSelectedRooms >= tempRoomData?.roomPlanData?.availableRooms &&
      !switchRoom
    ) {
      if (
        tempRoomData?.ratePlanData?.length > 1 &&
        tempRoomData?.roomPlanData?.availableRooms === 1
      ) {
        // switchRoom = true;
        operationType = 'SWITCH_WITHIN_ROOM';
      } else {
        // TODO these many rooms you can add
        tempRoomData.disableAddRoom = true;
        roomData.disableAddRoom = true;
        return;
      }
    }

    if (tempRatePlan?.minEntity > 1) {
      tempRatePlan.selectedRooms.push({
        guest: 2,
      });
    } else if (tempRatePlan?.minEntity === 1) {
      tempRatePlan.selectedRooms.push({
        guest: 1,
      });
    } else {
      tempRatePlan.selectedRooms.push({
        guest: 2,
      });
    }

    const tempRoomContainers: ClientProductRoomModel[] = JSON.parse(
      JSON.stringify(this.compData?.container?.roomContainers),
    );

    tempRoomContainers[roomIndex] = tempRoomData;

    this.fireSelectRoomEvent(tempRoomContainers, operationType);
  }

  removeSingleRoomInRatePlan(
    roomData: ClientProductRoomModel,
    ratePlan: ClientProductRatePlanModel,
    selectedRoomIndex: number,
  ) {
    ratePlan?.selectedRooms?.splice(selectedRoomIndex, 1);
    this.setRoomDisableButton(roomData);
    this.fireSelectRoomEvent(this.compData?.container?.roomContainers);
  }

  fireSelectRoomEvent(
    roomContainers: ClientProductRoomModel[],
    operationType?: string,
  ) {
    let selectedRatePlans: ClientProductRatePlanModel[] = [];
    for (const roomContainer of roomContainers) {
      selectedRatePlans = [
        ...selectedRatePlans,
        ...roomContainer?.ratePlanData?.filter(
          (item: ClientProductRatePlanModel) => {
            return item?.selectedRooms?.length > 0;
          },
        ),
      ];
    }

    this.eventService.toggleRoomActionEvent.emit({
      selectedRatePlans,
      operationType,
    });
  }

  setRoomDisableButton(roomData: ClientProductRoomModel) {
    let totalSelectedRooms = 0;
    for (const ratePlanTemp of roomData?.ratePlanData) {
      const selectedRoomCount: number =
        ratePlanTemp?.selectedRooms?.length > 0
          ? ratePlanTemp?.selectedRooms?.length
          : 0;
      totalSelectedRooms = totalSelectedRooms + selectedRoomCount;
    }

    if (totalSelectedRooms >= roomData?.roomPlanData?.availableRooms) {
      if (roomData?.roomPlanData?.availableRooms > 1) {
        roomData.disableAddRoom = true;
      } else {
        roomData.disableOnlyAddRoom = true;
      }
    } else {
      roomData.disableAddRoom = false;
      roomData.disableOnlyAddRoom = false;
    }
  }

  updateRoomDataFromRoomDataContainer(
    dbRoomDataContainers?: ClientProductRoomModel[],
  ) {
    for (const dbRoomDataContainer of dbRoomDataContainers) {
      const foundedRoom: ClientProductRoomModel =
        this.compData?.container?.roomContainers?.find(
          (item: ClientProductRoomModel) => {
            return (
              item?.roomPlanData?.roomId ===
              dbRoomDataContainer?.roomPlanData?.roomId
            );
          },
        );
      if (foundedRoom) {
        // update room data
        foundedRoom.roomPlanData.availableRooms =
          dbRoomDataContainer.roomPlanData.availableRooms;

        // handle rate plan data
        if (dbRoomDataContainer?.ratePlanData?.length > 0) {
          let ratePlanIndex = 0;
          for (const ratePlan of foundedRoom?.ratePlanData) {
            const foundedDbRatePlan: ClientProductRatePlanModel =
              dbRoomDataContainer?.ratePlanData?.find(
                (dbRatePlanItem: ClientProductRatePlanModel) => {
                  return dbRatePlanItem?.productId === ratePlan?.productId;
                },
              );
            if (foundedDbRatePlan) {
              // ratePlan = JSON.parse(JSON.stringify(foundedDbRatePlan));
              foundedRoom.ratePlanData[ratePlanIndex] = foundedDbRatePlan;
            }
            ratePlanIndex++;
          }
        }

        this.setRoomDisableButton(foundedRoom);
      }
    }
  }

  onSelectGuestAndChild(
    selectedValue: SelectedItem,
    roomData: ClientProductRoomModel,
    selectedRoom: ClientSelectedRoom,
    ratePlan: ClientProductRatePlanModel,
    ratePlanIndex: number,
    selectedRoomIndex: number,
  ) {
    selectedRoom.guest = selectedValue?.value?.totalAdults;
    selectedRoom.childrenCount = selectedValue?.value?.totalChildrenCount;
    selectedRoom.childrenPerRoom = selectedValue?.value?.totalChilds;
    this.fireSelectRoomEvent(this.compData?.container?.roomContainers);
  }

  previousImage(selectedImageCount) {
    selectedImageCount.value = Number(selectedImageCount.value) - 1;
  }

  nextImage(selectedImageCount) {
    selectedImageCount.value = Number(selectedImageCount.value) + 1;
  }

  openRoomViewDetail(
    roomData: ClientProductRoomModel,
    roomIndex: number,
    hideModal: boolean,
  ) {
    if (!hideModal) {
      this.selectedRoomContainer = roomData;
      this.selectedRoomContainerIndex = roomIndex;
      this.openIndividualRoomModal();
    }
  }

  openIndividualRoomModal() {
    if (this.individualRoomModal) {
      this.roomsOpenInModal = true;
      this.individualRoomModal?.present().then();
    } else {
      if (!this.b2bDeviceUtilService?.isServer()) {
        setTimeout(() => {
          this.roomsOpenInModal = true;
          this.individualRoomModal.present().then();
        }, 1000);
      }
    }
  }

  dismissIndividualRoomModal() {
    this.roomsOpenInModal = false;
    if (this.individualRoomModal?.isCmpOpen) {
      this.individualRoomModal?.dismiss()?.then();
    }
  }

  onIndividualModalWillDismiss($event: any) {
    this.roomsOpenInModal = false;
  }

  openMultipleRoomWarningModal() {
    if (this.multipleRoomWarningModal) {
      this.multipleRoomWarningModal?.present().then();
    } else {
      if (!this.b2bDeviceUtilService?.isServer()) {
        setTimeout(() => {
          this.multipleRoomWarningModal.present().then();
        }, 1000);
      }
    }
  }

  dismissMultipleRoomWarningModal() {
    if (this.multipleRoomWarningModal?.isCmpOpen) {
      this.multipleRoomWarningModal?.dismiss()?.then();
    }
  }

  addMultipleRoom(isMultiple: boolean) {
    if (isMultiple) {
      this.selectRoomAndAddRoom(
        this.multipleRoomWarningState?.roomData,
        this.multipleRoomWarningState?.ratePlan,
        this.multipleRoomWarningState?.ratePlanIndex,
        this.multipleRoomWarningState?.roomIndex,
        false,
      );
    } else {
      this.selectRoomAndAddRoom(
        this.multipleRoomWarningState?.roomData,
        this.multipleRoomWarningState?.ratePlan,
        this.multipleRoomWarningState?.ratePlanIndex,
        this.multipleRoomWarningState?.roomIndex,
        true,
      );
    }

    this.multipleRoomWarningState = undefined;
    this.dismissMultipleRoomWarningModal();
  }

  onRatePlanFilter(ratePlanFilter: KeyValueFilter) {
    this.loaderUtilService.show(undefined, {
      duration: 500,
    });
    this.emptyRooms = [];
    if (
      !ratePlanFilter ||
      ratePlanFilter?.key === this.selectedRatePlanFilter?.key
    ) {
      this.selectedRatePlanFilter = undefined;
    } else {
      const tempRoomContainers: ClientProductRoomModel[] = JSON.parse(
        JSON.stringify(this.compData?.container?.roomContainers),
      );
      for (const roomContainer of tempRoomContainers) {
        const ratePlans: ClientProductRatePlanModel[] =
          roomContainer?.ratePlanData?.filter(
            (ratePlan: ClientProductRatePlanModel) => {
              return (
                ratePlanFilter?.key === ratePlan?.ratePlanType ||
                ratePlan?.selectedRooms?.length > 0
              );
            },
          );

        if (!ratePlans || ratePlans?.length === 0) {
          this.emptyRooms?.push(roomContainer?.key);
        }
      }

      this.selectedRatePlanFilter = JSON.parse(JSON.stringify(ratePlanFilter));
    }
  }

  ngOnDestroy(): void {
    if (this.roomDataContainerUpdatedSubscription) {
      this.roomDataContainerUpdatedSubscription.unsubscribe();
    }
    if (this.refreshDataSubscription) {
      this.refreshDataSubscription?.unsubscribe();
    }
  }
}
